import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useT, type Lang } from "@/lib/i18n";
import { scrollToId } from "@/lib/scroll";

const SECTIONS = ["music", "artists", "licensing", "about", "contact"] as const;

export const Nav = ({ lang }: { lang: Lang }) => {
  const t = useT();
  const [open, setOpen] = useState(false);

  const go = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(id);
  };

  const langCls = (l: Lang) =>
    `transition-colors duration-300 ${
      lang === l ? "text-if-gold" : "text-if-dim hover:text-if-text"
    }`;

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-if-line bg-if-black/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-12">
        <a
          href="#top"
          onClick={go("top")}
          data-testid="nav-brand"
          className="flex items-center gap-3"
        >
          <span className="inline-block h-1.5 w-1.5 rotate-45 bg-if-gold" />
          <span className="text-[11px] font-medium uppercase tracking-[0.35em] text-if-text">
            {t.brand.name}
          </span>
        </a>
        <nav className="hidden items-center gap-8 lg:flex">
          {SECTIONS.map((s) => (
            <a
              key={s}
              href={`#${s}`}
              onClick={go(s)}
              data-testid={`nav-${s}-link`}
              className="text-[11px] uppercase tracking-[0.25em] text-if-dim transition-colors duration-300 hover:text-if-gold"
            >
              {t.nav[s]}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-6">
          <div
            className="flex items-center gap-2 font-sans text-[11px]"
            data-testid="language-switcher"
          >
            <Link to="/en" data-testid="lang-en-link" className={langCls("en")}>
              EN
            </Link>
            <span className="text-if-dim/40">/</span>
            <Link to="/tr" data-testid="lang-tr-link" className={langCls("tr")}>
              TR
            </Link>
          </div>
          <button
            data-testid="nav-menu-toggle"
            aria-label={open ? t.nav.close : t.nav.menu}
            onClick={() => setOpen(!open)}
            className="text-if-text transition-colors duration-300 hover:text-if-gold lg:hidden"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>
      {open && (
        <nav
          data-testid="nav-mobile-menu"
          className="border-t border-if-line bg-if-black px-6 py-8 lg:hidden"
        >
          <div className="flex flex-col gap-6">
            {SECTIONS.map((s) => (
              <a
                key={s}
                href={`#${s}`}
                onClick={go(s)}
                data-testid={`nav-mobile-${s}-link`}
                className="text-xs uppercase tracking-[0.3em] text-if-dim transition-colors duration-300 hover:text-if-gold"
              >
                {t.nav[s]}
              </a>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
};
